import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useClasses from "hooks/useClasses";
import { getAnalyticsSummary } from "store/actions/analytics";
import MetricCard from "./MetricCard";
import AnalyticsFilters from "./components/AnalyticsFilters";
import styles from "./analyticsStyle";
import { CircularProgress, Box, Typography, useTheme, Table, TableBody, TableCell, TableHead, TableRow, Paper } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AppleIcon from "@mui/icons-material/Apple";
import ShopIcon from "@mui/icons-material/Shop";

export default function AnalyticsSummary() {
  const classes = useClasses(styles);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const history = useHistory();

  const { summary, analyticsFilters, analyticsLoading } = useSelector((s) => s.analytics);

  const fetchAll = (filters) => {
    dispatch(getAnalyticsSummary(filters, history));
  };

  useEffect(() => {
    if (analyticsFilters.period !== "custom") {
      fetchAll(analyticsFilters);
    }
  }, [analyticsFilters]); // re-fetch when filters change globally, except for custom

  const handleRefresh = () => {
    fetchAll(analyticsFilters);
  };

  const s = summary?.data || summary || {};
  const users = s.users || {};
  const meta = s.meta || {};
  const business = s.business || {};
  const downloads = s.downloads || s.downloads_breakdown || s.users?.downloads_breakdown || {};
  const dlIos = downloads.ios || s.downloads?.ios || {};
  const dlAnd = downloads.android || s.downloads?.android || {};
  const kpis = s.kpis || {};
  const quality = s.quality || {};
  const comp = s.comparison || {};

  const userAvail = users.availability || {};
  const businessAvail = business.availability || {};
  const kpiAvail = kpis.availability || {};
  const qualityAvail = quality.availability || {};

  // Aggregate all availability records for table
  const allAvailability = [
    ...Object.entries(userAvail).map(([k, v]) => ({ metric: `users.${k}`, ...v })),
    ...Object.entries(businessAvail).map(([k, v]) => ({ metric: `business.${k}`, ...v })),
    ...Object.entries(kpiAvail).map(([k, v]) => ({ metric: `kpis.${k}`, ...v })),
    ...Object.entries(qualityAvail).map(([k, v]) => ({ metric: `quality.${k}`, ...v })),
  ];

  if (analyticsLoading && !summary) {
    return <div className={classes.loadingOverlay}><CircularProgress size={32} sx={{ color: "#6366f1", mr: 1.5 }} /> Loading dashboard...</div>;
  }

  // Format crash/ANR rates safely if expressed as decimal (0.015 -> 1.5%) vs percentage point (1.5)
  const formatRate = (rate) => {
    if (rate === null || rate === undefined) return null;
    return rate > 1 ? Number(rate.toFixed(2)) : Number((rate * 100).toFixed(2));
  };

  return (
    <div className={classes.pageWrapper}>
      {/* ── Meta Banner ── */}
      {meta.generated_at && (
        <div className={classes.metaBanner}>
          <div className={classes.metaInfo}>
            <span>Generated: {new Date(meta.generated_at).toLocaleString()}</span>
            {meta.data_through && <span>Data Through: {new Date(meta.data_through).toLocaleDateString()}</span>}
          </div>
          {meta.is_partial && (
            <div className={classes.metaWarning}>
              <WarningAmberIcon style={{ fontSize: 16 }} />
              Partial Data
            </div>
          )}
        </div>
      )}

      {/* ── Control Bar ── */}
      <AnalyticsFilters classes={classes} onRefresh={handleRefresh} />

      {/* ── 1. Acquisition ── */}
      <Box mb={4}>
        <Typography variant="h6" style={{ color: isDark ? "#fff" : "#333", marginBottom: 16, fontWeight: 600 }}>
          1. Acquisition
        </Typography>
        <div className={classes.kpiGrid}>
          <MetricCard
            icon={<AppleIcon />}
            name="iOS First-Time Downloads"
            value={dlIos.first_time ?? dlIos.first_time_downloads}
            unit="downloads"
            availability={userAvail.ios_first_time}
            comparison={comp.ios_first_time}
            source="Apple App Store"
          />
          <MetricCard
            icon={<ShopIcon />}
            name="Android First-Time Installers"
            value={dlAnd.first_time_installers ?? dlAnd.first_time}
            unit="installs"
            availability={userAvail.android_first_time_installers}
            comparison={comp.android_first_time_installers}
            source="Google Play Store"
          />
          <MetricCard
            name="New Registered Users"
            value={users.new_registered ?? business.new_registered_users}
            unit="users"
            availability={userAvail.new_registered}
            comparison={comp.new_registered}
            source="OneCircle"
          />
          <MetricCard
            name="Total Registered Users"
            value={users.registered_total ?? users.total_registered}
            unit="users"
            availability={userAvail.registered_total}
            comparison={comp.registered_total}
            source="OneCircle"
          />
        </div>
      </Box>

      {/* ── 2. Engagement ── */}
      <Box mb={4}>
        <Typography variant="h6" style={{ color: isDark ? "#fff" : "#333", marginBottom: 16, fontWeight: 600 }}>
          2. Engagement
        </Typography>
        <div className={classes.kpiGrid}>
          <MetricCard
            name="Active Users"
            value={users.active_users}
            unit="users"
            availability={userAvail.active_users}
            comparison={comp.active_users}
            source="GA4"
          />
          <MetricCard
            name="Engagement Rate"
            value={kpis.engagement_rate}
            unit="%"
            availability={kpiAvail.engagement_rate}
            source="Derived (GA4)"
          />
          <MetricCard
            name="Sessions Per Active User"
            value={kpis.sessions_per_active_user}
            unit="sessions/user"
            availability={kpiAvail.sessions_per_active_user}
            source="Derived (GA4)"
          />
          <MetricCard
            name="Total Sessions"
            value={users.sessions}
            unit="sessions"
            availability={userAvail.sessions}
            comparison={comp.sessions}
            source="GA4"
          />
          <MetricCard
            name="Engaged Sessions"
            value={users.engaged_sessions}
            unit="sessions"
            availability={userAvail.engaged_sessions}
            comparison={comp.engaged_sessions}
            source="GA4"
          />
        </div>
      </Box>

      {/* ── 3. Recycling Conversion ── */}
      <Box mb={4}>
        <Typography variant="h6" style={{ color: isDark ? "#fff" : "#333", marginBottom: 16, fontWeight: 600 }}>
          3. Recycling Conversion
        </Typography>
        <div className={classes.kpiGrid}>
          <MetricCard
            name="Recycling Users"
            value={business.recycling_users}
            unit="users"
            availability={businessAvail.recycling_users}
            comparison={comp.recycling_users}
            source="OneCircle DB"
            tooltip="Distinct registered clients who created at least one request"
          />
          <MetricCard
            name="Completed Drop-off Users"
            value={business.completed_dropoff_users}
            unit="users"
            availability={businessAvail.completed_dropoff_users}
            comparison={comp.completed_dropoff_users}
            source="OneCircle DB"
            tooltip="Distinct registered clients whose drop-off was collected"
          />
          <MetricCard
            name="Request Completion Rate"
            value={kpis.request_completion_rate}
            unit="%"
            availability={kpiAvail.request_completion_rate}
            source="Derived"
            tooltip="Completed drop-offs / recycling requests"
          />
          <MetricCard
            name="Recycler Completion Rate"
            value={kpis.recycler_completion_rate}
            unit="%"
            availability={kpiAvail.recycler_completion_rate}
            source="Derived"
            tooltip="Completed drop-off users / recycling users"
          />
          <MetricCard
            name="Requests Per Recycler"
            value={kpis.requests_per_recycler}
            unit="req/user"
            availability={kpiAvail.requests_per_recycler}
            source="Derived"
            tooltip="Recycling requests / recycling users"
          />
          <MetricCard
            name="Recycling Requests"
            value={business.recycling_requests}
            unit="requests"
            availability={businessAvail.recycling_requests}
            comparison={comp.recycling_requests}
            source="OneCircle DB"
          />
          <MetricCard
            name="Completed Drop-offs"
            value={business.completed_dropoffs}
            unit="drop-offs"
            availability={businessAvail.completed_dropoffs}
            comparison={comp.completed_dropoffs}
            source="OneCircle DB"
          />
        </div>
      </Box>

      {/* ── 4. Impact and Efficiency ── */}
      <Box mb={4}>
        <Typography variant="h6" style={{ color: isDark ? "#fff" : "#333", marginBottom: 16, fontWeight: 600 }}>
          4. Impact and Efficiency
        </Typography>
        <div className={classes.kpiGrid}>
          <MetricCard
            name="Items Collected"
            value={business.collected_items}
            unit="items"
            availability={businessAvail.collected_items}
            comparison={comp.collected_items}
            source="OneCircle DB"
            tooltip="Sum of item_count for completed drop-offs"
          />
          <MetricCard
            name="Items Per Completed Drop-off"
            value={kpis.items_per_completed_dropoff}
            unit="items/drop-off"
            availability={kpiAvail.items_per_completed_dropoff}
            source="Derived"
            tooltip="Collected items / completed drop-offs"
          />
          <MetricCard
            name="Points Per Completed Drop-off"
            value={kpis.points_per_completed_dropoff}
            unit="pts/drop-off"
            availability={kpiAvail.points_per_completed_dropoff}
            source="Derived"
            tooltip="Reward points issued / completed drop-offs"
          />
          <MetricCard
            name="Voucher Redemption Rate"
            value={kpis.voucher_redemption_rate}
            unit="%"
            availability={kpiAvail.voucher_redemption_rate}
            source="Derived"
            tooltip="Vouchers redeemed / vouchers issued"
          />
          <MetricCard
            name="Reward Points Issued"
            value={business.reward_points_issued ?? business.points_issued}
            unit="points"
            availability={businessAvail.reward_points_issued ?? businessAvail.points_issued}
            comparison={comp.reward_points_issued ?? comp.points_issued}
            source="OneCircle DB"
          />
          <MetricCard
            name="Vouchers Issued"
            value={business.vouchers_issued}
            unit="vouchers"
            availability={businessAvail.vouchers_issued}
            comparison={comp.vouchers_issued}
            source="OneCircle DB"
          />
          <MetricCard
            name="Vouchers Redeemed"
            value={business.vouchers_redeemed}
            unit="vouchers"
            availability={businessAvail.vouchers_redeemed}
            comparison={comp.vouchers_redeemed}
            source="OneCircle DB"
          />
        </div>
      </Box>

      {/* ── 5. Quality ── */}
      <Box mb={4}>
        <Typography variant="h6" style={{ color: isDark ? "#fff" : "#333", marginBottom: 16, fontWeight: 600 }}>
          5. Quality
        </Typography>
        <div className={classes.kpiGrid}>
          <MetricCard
            name="Android Crash Rate"
            value={formatRate(quality.crash_rate)}
            unit="%"
            availability={qualityAvail.crash_rate}
            source="Play Vitals"
          />
          <MetricCard
            name="Android ANR Rate"
            value={formatRate(quality.anr_rate)}
            unit="%"
            availability={qualityAvail.anr_rate}
            source="Play Vitals"
          />
          <MetricCard
            name="Crash Count"
            value={quality.crash_count}
            unit="crashes"
            availability={qualityAvail.crash_count}
            source="Play Vitals"
          />
          <MetricCard
            name="ANR Count"
            value={quality.anr_count}
            unit="ANRs"
            availability={qualityAvail.anr_count}
            source="Play Vitals"
          />
        </div>
      </Box>

      {/* ── Data Availability Details Table ── */}
      {allAvailability.length > 0 && (
        <Box mt={4} width="100%">
          <Typography variant="subtitle2" style={{ color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)", marginBottom: 12 }}>
            Data Availability & Provenance Details
          </Typography>
          <Paper style={{ background: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb", overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ color: isDark ? "#fff" : "#333", borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>Metric</TableCell>
                  <TableCell style={{ color: isDark ? "#fff" : "#333", borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>Status</TableCell>
                  <TableCell style={{ color: isDark ? "#fff" : "#333", borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>Source</TableCell>
                  <TableCell style={{ color: isDark ? "#fff" : "#333", borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>Unit</TableCell>
                  <TableCell style={{ color: isDark ? "#fff" : "#333", borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allAvailability.map((info, idx) => (
                  <TableRow key={idx}>
                    <TableCell style={{ color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>
                      {info.metric?.replace(/_/g, " ")}
                    </TableCell>
                    <TableCell style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>
                      {info.availability || "available"}
                    </TableCell>
                    <TableCell style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>
                      {info.source || "-"}
                    </TableCell>
                    <TableCell style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>
                      {info.unit || "-"}
                    </TableCell>
                    <TableCell style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>
                      {info.coverage_note || info.reason || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}
    </div>
  );
}
